import { Box, Typography, Chip, Stack, Button } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { baseNewAndDetailClasses, DisplayTable, metrcTagsClasses, roomDetailClasses } from '../../../../utility';
import apiService from '../../../../../api/apiService';
import { FRTLContext } from '../../../../../context/FRTLContext';
import { getFrtlAliasString } from '../../../../../utility/frtlUtils';
import { fetchFrtlData } from '../../../../../api/apiUtils';
import CreateBucketPopup from '../../Popup/CreateBucketPopup';
import BucketActionColumn from '../../../utils/BucketActionColumn';
import NoEntityMsg from '../../../../utility/messages/NoEntityMsg';
import { FeedbackContext } from '../../../../../App';
import { bucketsKeyTitleMap } from '../../../utils/TableTitles';
import DeleteAlertBox from '../../../../utility/messages/DeleteAlertBox';

const _BucketsTab = props => {
  const { currDarkThemeClass = '', handleErrorWithFeedback } = props;
  const [buckets, setBuckets] = useState([]);
  const [open, setOpen] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBucket, setCurrentBucket] = useState(null);
  const [bucketToDelete, setBucketToDelete] = useState(null);
  const [newBucketData, setNewBucketData] = useState({
    items: [],
    unit: [],
    bucket_categories: [],
    harvests: [],
  });
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('All');

  const { cropplanid } = useParams();
  const { frtlContextValue, setFrtlContextValue } = useContext(FRTLContext);
  const feedbackCX = useContext(FeedbackContext);

  const handleEditBucket = async bucket => {
    console.log('Edit bucket:', bucket);
    setCurrentBucket(bucket);
    setIsEditMode(true);
    setOpen(true);
  };

  const handleDeleteBucket = async bucket => {
    console.log('Delete bucket initiated:', bucket);
    setBucketToDelete(bucket);
    setDeletePopup(true);
  };

  const confirmDeleteBucket = async () => {
    try {
      await apiService.deleteInstance('cropplan-buckets/', {
        bucket_ids: [bucketToDelete?.id],
      });
      fetchBuckets();
      feedbackCX.setContextValue(true, {
        type: 'success',
        message: 'Bucket deleted successfully.',
      });
      setDeletePopup(false);
      setBucketToDelete(null);
    } catch (error) {
      console.error('Error deleting bucket:', error);
      if (handleErrorWithFeedback) {
        handleErrorWithFeedback('Failed to delete bucket');
      }
      setDeletePopup(false);
      setBucketToDelete(null);
    }
  };

  const fetchBuckets = async () => {
    try {
      const response = await apiService.fetchInstance('cropplan-buckets/?cropplan_id=' + cropplanid);
      setBuckets(response?.data || []);
    } catch (error) {
      console.error('Error fetching buckets:', error);
      if (handleErrorWithFeedback) {
        handleErrorWithFeedback('Failed to fetch buckets');
      }
    }
  };

  const fetchInstances = async () => {
    try {
      if (props?.values?.strains?.[0]?.farmstrain) {
        const response = await apiService.fetchInstance(`new/buckets/?strain=${props?.values?.strains?.[0]?.farmstrain}&cropplan=${cropplanid}`);
        setNewBucketData(response?.data || { items: [], unit: [], bucket_categories: [], harvests: [] });
      }
    } catch (error) {
      console.error('Error fetching bucket instances:', error);
    }
  };

  useEffect(() => {
    if (!frtlContextValue) {
      fetchFrtlData()
        .then(data => {
          setFrtlContextValue(data || {});
        })
        .catch(error => {
          console.error('Error loading FRTL data:', error);
          setFrtlContextValue({});
        });
    }
  }, [frtlContextValue, setFrtlContextValue]);

  useEffect(() => {
    fetchBuckets();
  }, [cropplanid]);

  useEffect(() => {
    fetchInstances();
  }, [cropplanid, props?.values?.strains, open]);

  const getUnitName = unitId => {
    const unit = newBucketData.unit?.find(u => u.id === unitId);
    return unit ? unit.unit_name : unitId;
  };

  const getItemName = itemId => {
    const item = newBucketData.items?.find(i => i.id === itemId);
    return item ? item.item_name : itemId;
  };

  const getBucketCategoryName = categoryId => {
    const category = newBucketData.bucket_categories?.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  const getRoutineType = value => {
    return value === 'post_harvest' ? 'Post Harvest' : 'Fine Trimming';
  };

  const getHarvestNames = harvestIds => {
    if (!Array.isArray(harvestIds) || harvestIds.length === 0) {
      return 'No harvests';
    }

    const nameSet = new Set();

    harvestIds.forEach(harvestId => {
      const harvest = newBucketData.harvests?.find(h => h.id === harvestId);
      let name = null;
      if (harvest?.harvest_name == null) {
        const harvest2 = newBucketData.harvests?.find(h2 => h2.metrc_harvest_id === harvest?.metrc_harvest_id);
        name = harvest2?.harvest_name;
      } else {
        name = harvest?.harvest_name;
      }
      if (name) {
        nameSet.add(name);
      }
    });

    return Array.from(nameSet).join(', ') || 'No harvests';
  };

  const handleCategoryFilterChange = categoryId => {
    setSelectedCategoryFilter(categoryId);
  };

  const bucketsTableTitles = useMemo(() => bucketsKeyTitleMap.map(e => ({ title: e.title })), []);

  const filteredBuckets = useMemo(() => {
    if (selectedCategoryFilter === 'All') {
      return buckets;
    }

    return buckets.filter(bucket => {
      const categoryName = getBucketCategoryName(bucket.bucket_categories);
      return categoryName === selectedCategoryFilter;
    });
  }, [buckets, selectedCategoryFilter, newBucketData.bucket_categories]);

  const bucketsTableEntries = useMemo(() => {
    return filteredBuckets.map((bucket, idx) => {
      return bucketsKeyTitleMap.map(keyTitle => {
        if (keyTitle.key === 'serial_number') {
          return idx + 1;
        }

        if (keyTitle.key === 'actions') {
          return (
            <BucketActionColumn bucket={bucket} currDarkThemeClass={currDarkThemeClass} onEdit={handleEditBucket} onDelete={handleDeleteBucket} />
          );
        }

        const value = bucket[keyTitle.key];

        if (keyTitle.key === 'frtl') {
          return getFrtlAliasString(value, frtlContextValue);
        }

        if (keyTitle.key === 'bucket_unit_of_measurement') {
          return getUnitName(value);
        }

        if (keyTitle.key === 'item') {
          return getItemName(value);
        }

        if (keyTitle.key === 'bucket_categories') {
          return getBucketCategoryName(value);
        }

        if (keyTitle.key === 'routine_type') {
          return getRoutineType(value);
        }

        if (keyTitle.key === 'harvests') {
          return getHarvestNames(value);
        }

        if (keyTitle.key === 'created_on' || keyTitle.key === 'updated_on') {
          return value ? new Date(value).toLocaleDateString() : '';
        }

        return value ?? '';
      });
    });
  }, [filteredBuckets, frtlContextValue, newBucketData]);

  const shouldShowEmptyTable = useMemo(() => {
    return !newBucketData.harvests || newBucketData.harvests.length === 0;
  }, [newBucketData.harvests]);

  const bucketsTableProps = {
    currDarkThemeClass,
    tableTitles: bucketsTableTitles,
    tableEntries: shouldShowEmptyTable ? [] : bucketsTableEntries,
    emptyTableMsg: shouldShowEmptyTable ? <NoEntityMsg /> : 'No buckets available',
    toShowAddBtn: false,
  };

  const bucketCategoryFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
    padding: '10px',
  };

  const bucketCategoryFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const categoryChipsProps = category => ({
    key: category.id,
    label: category.name,
    variant: selectedCategoryFilter === category.name ? 'filled' : 'outlined',
    onClick: () => handleCategoryFilterChange(category.name),
    clickable: true,
    className: `${roomDetailClasses?.chip} ${currDarkThemeClass}`,
  });

  const bucketCategoryChipsList = (
    <Stack direction="row" spacing={1}>
      <Chip
        key="All"
        label="All"
        variant={selectedCategoryFilter === 'All' ? 'filled' : 'outlined'}
        onClick={() => handleCategoryFilterChange('All')}
        clickable
        className={`${roomDetailClasses?.chip} ${currDarkThemeClass}`}
      />
      {newBucketData.bucket_categories?.map(category => (
        <Chip key={category.id} {...categoryChipsProps(category)} />
      ))}
    </Stack>
  );

  const handleClosePopup = () => {
    setOpen(false);
    setIsEditMode(false);
    setCurrentBucket(null);
  };

  const handleUpdateBucket = async bucketData => {
    try {
      console.log('Updating bucket:', bucketData);
      const data = await apiService.modifyInstance(`cropplan-buckets/${currentBucket.id}/`, {
        ...bucketData,
      });

      if (data?.status === 200) {
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Bucket updated successfully.',
        });
      }

      fetchBuckets();
      handleClosePopup();
    } catch (error) {
      console.error('Error updating bucket:', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Failed to update bucket.',
      });
    }
  };

  const createBucketPopupProps = {
    currDarkThemeClass,
    open: open,
    onClose: handleClosePopup,
    newBucketData: newBucketData,
    parentValues: props.values,
    cropplanid: cropplanid,
    isEditMode: isEditMode,
    currentBucket: currentBucket,
    onEditBucket: handleEditBucket,
    onDeleteBucket: handleDeleteBucket,
    handleAddBucket: isEditMode
      ? handleUpdateBucket
      : async bucketData => {
          console.log('Saving bucket:', bucketData);
          const data = await apiService.createInstance('cropplan-buckets/', {
            ...bucketData,
          });
          if (data?.status === 201) {
            feedbackCX.setContextValue(true, {
              type: 'success',
              message: 'Bucket Created Successfully.',
            });
          }
          fetchBuckets();
          setOpen(false);
        },
  };

  const noEntityMsgProps = {
    headContent: 'No harvests have been completed in this Crop Run.',
    msg: 'Buckets will be available once you complete the Wet Weight routine.',
  };

  const plantSubTypoProps = {
    className: `${roomDetailClasses?.tabTitleTypo} ${currDarkThemeClass}`,
  };

  const getBucketDisplayName = bucket => {
    if (!bucket) return '';

    const itemName = getItemName(bucket.item);
    const categoryName = getBucketCategoryName(bucket.bucket_categories);
    const unitName = getUnitName(bucket.bucket_unit_of_measurement);

    let displayName = '';
    if (itemName && itemName !== bucket.item) {
      displayName = itemName;
    } else if (categoryName && categoryName !== bucket.bucket_categories) {
      displayName = categoryName;
    } else if (bucket.name) {
      displayName = bucket.name;
    } else if (bucket.id) {
      displayName = `Bucket #${bucket.id}`;
    } else {
      displayName = 'this bucket';
    }

    return displayName;
  };

  const deleteAlertBoxProps = {
    open: deletePopup,
    onClose: () => {
      setDeletePopup(false);
      setBucketToDelete(null);
    },
    onDelete: confirmDeleteBucket,
    currDarkThemeClass,
    deleteBtn: 'Delete Bucket',
    cancelBtn: 'Cancel',
    title: `Are you sure you want to delete ${getBucketDisplayName(bucketToDelete?.name)}?`,
    headContentText: 'This action cannot be undone.',
  };

  return (
    <Box className={`${baseNewAndDetailClasses?.tableContainer} ${currDarkThemeClass}`}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" padding={1} width="100%">
        <Typography {...plantSubTypoProps}>Buckets</Typography>
        <Button
          onClick={() => {
            setIsEditMode(false);
            setCurrentBucket(null);
            setOpen(true);
          }}
          disabled={shouldShowEmptyTable}
          title={shouldShowEmptyTable ? 'Please create harvests before creating buckets' : 'Create a new bucket'}
        >
          Create Bucket
        </Button>
      </Stack>

      {shouldShowEmptyTable && <NoEntityMsg {...noEntityMsgProps} />}

      {!shouldShowEmptyTable && (
        <>
          <Stack {...bucketCategoryFilterProps}>
            <Typography {...bucketCategoryFilterTypoProps}>Filter by Category:</Typography>
            {bucketCategoryChipsList}
          </Stack>

          <DisplayTable {...bucketsTableProps} />
        </>
      )}

      {open && <CreateBucketPopup {...createBucketPopupProps} />}
      {deletePopup && <DeleteAlertBox {...deleteAlertBoxProps} />}
    </Box>
  );
};

export default _BucketsTab;
